package com.swetdot.swetgram.ui.groups

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.groups.GroupRepository
import com.swetdot.swetgram.data.groups.asGroupFailure
import com.swetdot.swetgram.data.groups.isPlausibleInviteToken
import com.swetdot.swetgram.domain.GroupFailure
import com.swetdot.swetgram.domain.GroupId
import com.swetdot.swetgram.domain.GroupInvite
import com.swetdot.swetgram.domain.GroupInviteAction
import com.swetdot.swetgram.domain.GroupJoinOutcome
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/**
 * Where an invite link lands: `previewGroupInvite`, then `actOnGroupInvite`.
 *
 * THE ROUTE THE NAVIGATION HOST MUST REGISTER, since this screen is the one surface in the app that
 * is reached from OUTSIDE it:
 *
 *   in-app route : `join/{invite_token}`
 *   universal link: `https://swetgram.com/join/{invite_token}`  (https, host `swetgram.com`,
 *                   path prefix `/join/`)
 *
 * The host is the contract's, not the folder's: `InviteLink.url`'s own example in `openapi.yaml` is
 * `https://swetgram.com/join/example-token`, while STA §9.2 writes `https://swetgram.app/...`. The
 * CONTRACT WINS and the server builds `swetgram.com`, so registering `.app` would catch nothing.
 * The token is one path segment of at least 20 characters from the unreserved set, so it needs no
 * escaping and must be passed through verbatim -- lower-casing or trimming it would break a
 * case-sensitive credential.
 *
 * THIS SCREEN IS REACHABLE WHILE SIGNED OUT, in the sense that the link is. Both operations are
 * under the contract's global `bearerAuth` -- neither declares `security: []` -- so an unauthorised
 * caller gets 401 and the app's existing session handling sends them to sign in. Nothing here
 * pretends otherwise, and no call in this file passes `authenticated = false`.
 *
 *
 * THE STATES, AND WHICH OF THEM ARE TERMINAL
 *
 * Four answers cannot be retried and are not reported as errors, because "Try again" would be a
 * lie: the link does not work, you are banned, the group is full, your email is unverified. They are
 * [JoinBlock], and the screen shows one sentence and no action. Everything else -- offline, a
 * timeout, a 5xx, a rate limit -- is an ordinary banner with Retry over whatever is on screen.
 *
 * A BANNED USER LOOKS EXACTLY LIKE ANYBODY ELSE UNTIL THEY PRESS THE BUTTON, and that is the
 * contract's design rather than an oversight here. `GroupInvitePreview.action` is a closed enum with
 * no banned value, and DECISIONS §7 explains what a fifth value would cost: a preview that refused
 * would tell whoever holds the link whether the group had banned that person, and would turn the
 * preview into a probe. So the preview offers Join, and the POST answers `403 group_banned`.
 */

// ---------------------------------------------------------------------------------------------
// The rules -- pure, so they can be tested without a repository
// ---------------------------------------------------------------------------------------------

/**
 * An answer this screen cannot do anything about.
 *
 * Separate from an error banner on purpose. A banner offers Retry and implies the request might
 * work next time; every value here is a settled decision about this person and this group, and
 * offering to try again would just spend the rate limit finding out the same thing.
 *
 * PUBLIC where `GroupExitOption` is internal, and the difference is structural rather than a change
 * of mind: this one is a CONSTRUCTOR property of [JoinGroupUiState], and a public data class cannot
 * carry an internal type through its primary constructor, its `copy` or its `componentN`.
 * `GroupExitOption` is reached through a computed `internal val` and has no such problem.
 */
enum class JoinBlock {
    /**
     * The link does not resolve to a group anybody can join.
     *
     * ONE STATE FOR FIVE CAUSES -- never issued, rotated away, expired, pointing at a deleted
     * group, or not the right shape at all -- because the server answers one code for four of them
     * and the client refuses the fifth without asking. Splitting them on screen would leak what the
     * server deliberately does not say: that a rotated token was once real.
     */
    LINK_UNUSABLE,

    /** `403 group_banned`. Only the POST can say this; the preview cannot and does not try. */
    BANNED,

    /** `409 group_full`. DECISIONS §7's 500-member cap, checked when somebody actually joins. */
    GROUP_FULL,

    /** `403 email_not_verified`. `actOnGroupInvite` is `x-requires-verified-email`. */
    EMAIL_NOT_VERIFIED,
}

/**
 * Maps a thrown failure to a terminal state, or null when it is an ordinary retryable error.
 *
 * [GroupFailure.INVALID] is folded into [JoinBlock.LINK_UNUSABLE]: a 400 on this path means the
 * token failed the contract's `minLength: 20`, which is a fact about the link rather than about
 * the request, and the user's remedy is a different link either way.
 */
internal fun joinBlockFor(failure: GroupFailure): JoinBlock? = when (failure) {
    GroupFailure.UNAVAILABLE, GroupFailure.INVALID -> JoinBlock.LINK_UNUSABLE
    GroupFailure.BANNED -> JoinBlock.BANNED
    GroupFailure.GROUP_FULL -> JoinBlock.GROUP_FULL
    GroupFailure.EMAIL_NOT_VERIFIED -> JoinBlock.EMAIL_NOT_VERIFIED
    else -> null
}

/** SDS §18.2: say what happened and what to do next, and never blame the user for a dead link. */
// TODO(localization): every string in this file moves into resources for EN + RU.
internal fun joinBlockTitle(block: JoinBlock): String = when (block) {
    JoinBlock.LINK_UNUSABLE -> "This invite link does not work."
    JoinBlock.BANNED -> "You cannot join this group."
    JoinBlock.GROUP_FULL -> "This group is full."
    JoinBlock.EMAIL_NOT_VERIFIED -> "Verify your email first."
}

internal fun joinBlockBody(block: JoinBlock): String = when (block) {
    // Deliberately does not speculate about WHICH of the causes it was. The server does not say,
    // and a client that guessed would be guessing out loud.
    JoinBlock.LINK_UNUSABLE ->
        "It may have been replaced by a newer one. Ask whoever shared it for the current link."
    JoinBlock.BANNED ->
        "An admin of this group has removed you from it. Only they can undo that."
    JoinBlock.GROUP_FULL ->
        "A group can have 500 members. Someone has to leave before anyone else can join."
    JoinBlock.EMAIL_NOT_VERIFIED ->
        "Joining a group needs a verified email address. Verify yours and open this link again."
}

/**
 * The label on the one action, or null when there is nothing to press.
 *
 * Null covers three different situations and all three are correct with no button: the preview has
 * not arrived, the request is already waiting for an admin, and the action was a value this build
 * could not read. The last is the fail-closed direction -- an unreadable action must never default
 * to "Join", because that is a membership nobody asked for.
 */
internal fun joinActionLabel(action: GroupInviteAction?): String? = when (action) {
    GroupInviteAction.JOIN -> "Join group"
    GroupInviteAction.REQUEST_ACCESS -> "Request to join"
    GroupInviteAction.ALREADY_MEMBER -> "Open group"
    GroupInviteAction.REQUEST_PENDING -> null
    null -> null
}

/**
 * The sentence under the group's name: what pressing the button will do, or what state you are in.
 *
 * The private-group case is the one that has to be said out loud. "Request to join" does not tell
 * anybody that a human has to act, or that it may simply never happen.
 */
internal fun joinActionExplanation(action: GroupInviteAction?): String = when (action) {
    GroupInviteAction.JOIN ->
        "You will join straight away. You will see messages sent from now on, not the group's " +
            "earlier history."
    GroupInviteAction.REQUEST_ACCESS ->
        "An admin has to approve you before you can see anything in this group."
    GroupInviteAction.REQUEST_PENDING ->
        "Your request is waiting for an admin. You will see the group here once they approve it."
    GroupInviteAction.ALREADY_MEMBER ->
        "You are already in this group."
    // Nothing true can be said about what a button would do, so the sentence is about the app
    // rather than about the group. It takes no parameter from the group for the same reason.
    null ->
        "This app could not read what this link offers. Update Swetgram and open it again."
}

// ---------------------------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------------------------

data class JoinGroupUiState(
    val invite: GroupInvite? = null,
    val loadedOnce: Boolean = false,
    /** The join or the request is in flight. */
    val busy: Boolean = false,
    /** A settled answer with no action attached. See [JoinBlock]. */
    val blocked: JoinBlock? = null,
    /**
     * A request is on file: either the preview said so, or this session just filed one, or the
     * server answered `409 join_request_pending`.
     *
     * All three are the same fact to the user, which is why the 409 is not an error here.
     */
    val requested: Boolean = false,
    /**
     * ONE-SHOT: the caller navigates to this group and calls [JoinGroupViewModel.consumeJoined].
     *
     * Set both by a successful join and by "Open group" on a link the viewer is already a member
     * of -- the destination is the same and the second needs no request at all.
     */
    val joinedGroupId: GroupId? = null,
    val error: String? = null,
) {
    /**
     * What the button should do NOW, which is not always what the preview said.
     *
     * A request filed in this session overrides the preview's `request_access`, so the button
     * disappears and the waiting-for-an-admin sentence takes its place without a refetch.
     */
    internal val action: GroupInviteAction?
        get() = when {
            requested -> GroupInviteAction.REQUEST_PENDING
            else -> invite?.action
        }

    val loading: Boolean get() = invite == null && !loadedOnce && blocked == null
}

/**
 * SPS §12.3 and SDS §27.8's "support join via invite link". One token, one group, one decision.
 */
class JoinGroupViewModel(
    /** The raw path segment from the link. Never trimmed, lower-cased or otherwise normalised. */
    private val token: String,
    private val groups: GroupRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(JoinGroupUiState())
    val state: StateFlow<JoinGroupUiState> = _state.asStateFlow()

    private var loading = false

    init {
        refresh()
    }

    /**
     * `previewGroupInvite`. Safe on entry -- it is a GET and it writes nothing, which is the whole
     * point of a preview.
     *
     * A TOKEN THAT CANNOT BE ONE NEVER LEAVES THE DEVICE. The check is the repository's, reused
     * here so the screen reaches its terminal state without a request: a string that fails it is
     * not a token this server minted, and asking would spend a round trip to be told so. The user
     * sees exactly what a server 404 produces, because telling them their link was the wrong LENGTH
     * would publish how long a real one is.
     */
    fun refresh() {
        if (loading) return

        if (!isPlausibleInviteToken(token.trim())) {
            _state.value = _state.value.copy(loadedOnce = true, blocked = JoinBlock.LINK_UNUSABLE)
            return
        }

        loading = true
        _state.value = _state.value.copy(error = null)
        viewModelScope.launch {
            try {
                val invite = groups.invitePreview(token)
                _state.value = _state.value.copy(
                    invite = invite,
                    loadedOnce = true,
                    blocked = null,
                    // The preview is the authority on this until a request is actually filed.
                    requested = invite.action == GroupInviteAction.REQUEST_PENDING,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                val block = joinBlockFor(error.asGroupFailure())
                _state.value = _state.value.copy(
                    loadedOnce = true,
                    blocked = block,
                    // A terminal answer replaces the banner rather than sitting under one: there is
                    // nothing to retry, so a Retry button beside it would be the wrong offer.
                    error = if (block == null) {
                        error.groupBanner("Could not open this invite link.")
                    } else {
                        null
                    },
                )
            } finally {
                loading = false
            }
        }
    }

    /**
     * `actOnGroupInvite`, or -- for somebody who is already in the group -- no request at all.
     *
     * NOT OPTIMISTIC. Nothing on screen changes until the server has answered, because the two
     * answers land the user in completely different places and guessing between them would show
     * somebody a group they are not in.
     *
     * `409 join_request_pending` IS TREATED AS SUCCESS, and that is the honest reading rather than
     * a shortcut: it means a request from this person is already on file, which is exactly the state
     * the button was trying to reach. The user does not care whether this tap or an earlier one put
     * it there. It is also, per DECISIONS §7, the only producer of that code in the whole API.
     */
    fun act() {
        val current = _state.value
        if (current.busy) return

        when (current.action) {
            // The destination is the same as a successful join and needs no write to get there.
            GroupInviteAction.ALREADY_MEMBER -> {
                _state.value = current.copy(joinedGroupId = current.invite?.groupId)
                return
            }
            GroupInviteAction.JOIN, GroupInviteAction.REQUEST_ACCESS -> Unit
            // Nothing to press: already waiting, or an action this build could not read.
            GroupInviteAction.REQUEST_PENDING, null -> return
        }

        _state.value = current.copy(busy = true, error = null)
        viewModelScope.launch {
            try {
                when (val outcome = groups.actOnInvite(token)) {
                    is GroupJoinOutcome.Joined -> _state.value = _state.value.copy(
                        busy = false,
                        // `Joined.group` is ignored on purpose. It is optional on the wire, the
                        // group screen loads the group anyway, and branching on its presence is
                        // exactly the mistake the DTO warns about -- `status` decides, not `group`.
                        joinedGroupId = outcome.groupId,
                    )
                    is GroupJoinOutcome.Requested -> _state.value = _state.value.copy(
                        busy = false,
                        requested = true,
                    )
                }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                val failure = error.asGroupFailure()
                if (failure == GroupFailure.JOIN_REQUEST_PENDING) {
                    _state.value = _state.value.copy(busy = false, requested = true)
                    return@launch
                }
                val block = joinBlockFor(failure)
                _state.value = _state.value.copy(
                    busy = false,
                    blocked = block,
                    error = if (block == null) {
                        error.groupBanner("Could not join this group.")
                    } else {
                        null
                    },
                )
            }
        }
    }

    /** Consumed by the caller BEFORE it navigates; see [JoinGroupUiState.joinedGroupId]. */
    fun consumeJoined() {
        _state.value = _state.value.copy(joinedGroupId = null)
    }

    fun dismissError() {
        _state.value = _state.value.copy(error = null)
    }
}
